import { useState } from "react";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useDailyRecords } from "@/hooks/useDailyRecords";
import { useWeightLogs } from "@/hooks/useWeightLogs";
import { AppLayout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { StatTile } from "@/components/StatTile";
import { LoadingScreen } from "@/components/LoadingScreen";
import { WeightChart } from "@/components/WeightChart";
import { DailyCheckinCard } from "@/components/DailyCheckinCard";
import { SymptomTrends } from "@/components/SymptomTrends";
import { RecordsCalendar } from "@/components/RecordsCalendar";
import type { DailyRecordRow } from "@/lib/database.types";

function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Consecutive-day streak ending today (or yesterday, so an unlogged "today"
// doesn't zero the count before the patient checks in tonight).
function streakOf(records: DailyRecordRow[]): number {
  const logged = new Set(records.map((r) => r.record_date));
  const cursor = new Date();
  if (!logged.has(localISO(cursor))) cursor.setDate(cursor.getDate() - 1);
  let streak = 0;
  while (logged.has(localISO(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export default function Progress() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const recordsQ = useDailyRecords(patientQ.data?.id);
  const weightQ = useWeightLogs(patientQ.data?.id);
  const todayISO = localISO(new Date());
  const [selectedDate, setSelectedDate] = useState(todayISO);

  if (patientQ.isLoading || recordsQ.isLoading) return <LoadingScreen />;

  const patient = patientQ.data ?? null;
  const records = recordsQ.data ?? [];
  const weightLogs = weightQ.data ?? [];
  const selectedRecord = records.find((r) => r.record_date === selectedDate) ?? null;

  const streak = streakOf(records);
  const latestWeight = weightLogs[0]?.weight_lb ?? null;
  const startingWeight = patient?.starting_weight_lb ?? null;
  const downLb =
    latestWeight != null && startingWeight != null
      ? Number(startingWeight) - Number(latestWeight)
      : null;

  return (
    <AppLayout>
      <PageHeader
        title="Progress"
        subtitle="Daily check-ins: weight, activity, and how the medication feels."
      />

      {/* Stat tiles */}
      <div className="flex gap-3.5 mb-4 flex-wrap">
        <StatTile label="Days logged" value={records.length} />
        <StatTile
          label="Current streak"
          value={streak > 0 ? `${streak} day${streak === 1 ? "" : "s"}` : "—"}
          hint={streak > 0 ? "keep it going" : "log today to start one"}
        />
        <StatTile
          label="Weight change"
          value={
            downLb != null
              ? `${downLb > 0 ? "−" : downLb < 0 ? "+" : ""}${Math.abs(downLb).toFixed(0)} lb`
              : "—"
          }
          hint={startingWeight != null ? `from ${Number(startingWeight).toFixed(0)} lb start` : undefined}
        />
      </div>

      {/* Day check-in — keyed by day + record so state resets when switching days. */}
      <div className="mb-4">
        <DailyCheckinCard
          key={`${selectedDate}:${selectedRecord?.id ?? "new"}`}
          patientId={patient?.id}
          date={selectedDate}
          record={selectedRecord}
          isToday={selectedDate === todayISO}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Symptom trends">
          <SymptomTrends records={records} />
        </Card>
        <Card title="Calendar" action={
          selectedDate !== todayISO ? (
            <button
              type="button"
              onClick={() => setSelectedDate(todayISO)}
              className="text-fh-green hover:text-fh-green-dark"
            >
              Back to today →
            </button>
          ) : undefined
        }>
          <RecordsCalendar
            records={records}
            selected={selectedDate}
            onSelect={setSelectedDate}
          />
        </Card>
        <div className="lg:col-span-2">
          <Card title="Weight trend">
            <WeightChart logs={weightLogs} />
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
